/* GRIG cart drawer decorations, the PDP upgrade step and the one-time PDP upsell popup.
   Cart writes go through GRIGCart only. Progress may be projected from cart
   quantities; any saving shown is read from Shopify's cart.js allocations.
   One rule set decides every offer: a box line is upgraded by swapping it, never by
   adding a second box; a filler never adds a box to a cart that has one; what the
   shopper declined or swapped away in this session is not offered again. */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) api.mount(root);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  var SESSION_KEY = 'grig_upsell_popup_v1', SKIP_KEY = 'grig_upsell_skip_v1';
  // The popup offers free shipping only when it is genuinely close (cents) and the
  // addition is proportionate to what was just added.
  var POPUP_SHIPPING_GAP = 1500, POPUP_PRICE_RATIO = 2, DRAWER_PRICE_RATIO = 2, DRAWER_OFFERS = 2, CROSS_SELL = 4;
  // How long a popup decision may wait for a fresh catalog after an addition.
  var POPUP_CATALOG_WAIT = 1500;
  // Pages where the one-time popup may follow an add (see popupAdd).
  var POPUP_PAGES = {product:true, index:true, collection:true};
  function id(value) { return String(value == null ? '' : value); }
  // "Dare Box — 5 packs" reads as "Dare Box" in buttons and headings.
  function shortTitle(title) {
    var text = String(title == null ? '' : title).trim(), cut = text.split(/\s+[—–·|]\s+|,\s+/)[0].trim();
    return cut || text;
  }
  function isBoxVariant(variant) { return !!(variant && (variant.knownBox || variant.product && variant.product.recipe)); }

  // Next box for a box line: strictly more sachets, higher price, recipe superset.
  // Only the very next step is offered: when the shopper declined it (or already has
  // it), nothing bigger is pushed instead.
  function nextBox(data, current, existing, allowed, exclude) {
    if (!data || !Array.isArray(data.boxes) || !current) return null;
    var next = data.boxes.filter(function (box) {
      return box !== current && box.count > current.count && box.variant.price > current.variant.price &&
        Object.keys(current.recipe).every(function (key) { return (box.recipe[key] || 0) >= current.recipe[key]; }) &&
        (!allowed || allowed(box.variant, 1, existing || {}));
    }).sort(function (a, b) { return a.count - b.count || a.variant.price - b.variant.price; })[0] || null;
    return next && exclude && exclude(next) ? null : next;
  }
  function boxFor(data, variantId) {
    return data && Array.isArray(data.boxes) ? data.boxes.find(function (box) { return id(box.variant.id) === id(variantId); }) || null : null;
  }
  function extras(data, current, target) {
    return Object.keys(target.recipe).map(function (key) {
      var more = target.recipe[key] - (current.recipe[key] || 0), variant = data.variants[key];
      return more > 0 && variant ? {variant:variant, quantity:more} : null;
    }).filter(Boolean);
  }
  function inCart(cart, variantId) {
    return !!(cart && Array.isArray(cart.items) && cart.items.some(function (line) { return id(line.variant_id || line.id) === id(variantId); }));
  }
  function cartHasBox(data, cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.some(function (line) {
      var key = id(line.variant_id || line.id);
      return line.has_components === true || !!boxFor(data, key) || isBoxVariant(data && data.variants && data.variants[key]);
    }));
  }
  // Aggregate cart.js discounts by title: line allocations first, order level as fallback.
  function discounts(cart) {
    var rows = {}, order = [];
    function add(title, amount) {
      if (typeof title !== 'string' || !title || !Number.isSafeInteger(amount) || amount <= 0) return;
      if (!rows[title]) { rows[title] = 0; order.push(title); }
      rows[title] += amount;
    }
    (cart && cart.items || []).forEach(function (line) {
      (line.line_level_discount_allocations || []).forEach(function (a) { add(a && a.discount_application && a.discount_application.title, a && a.amount); });
    });
    var lineTitles = order.slice();
    (cart && cart.cart_level_discount_applications || []).forEach(function (app) {
      if (app && lineTitles.indexOf(app.title) < 0) add(app.title, app.total_allocated_amount);
    });
    return order.map(function (title) { return {title:title, amount:rows[title]}; });
  }
  // A shipping suggestion must provably close the gap: only native mode projects the
  // total Shopify charges after the addition. Plain mode knows none of Shopify's automatic
  // discounts, and an addition can trigger one (a fifth sachet starts the sachet discount),
  // so the list price proves nothing there: no suggestion claims to close the gap.
  function shippingCloses(o) {
    return !!o && o.kind === 'shipping-gap' && !!o.candidateVariantId && o.projectedNative === true;
  }
  // Does swapping one unit of this box line reach free shipping? Native mode asks the
  // engine for Shopify's discounted total on the cart with the target box in place of
  // the current one: Black Friday is modelled on the whole cart, tier changes
  // included, and a code or any other unknown discount yields no projection. No
  // projection, no claim. Plain mode knows none of Shopify's automatic discounts, so a list
  // price sum proves nothing: it never claims free shipping for a swap (the shipping line
  // still states the remaining amount).
  function swapReachesShipping(cart, quote, box, target, line, project) {
    var ship = quote && quote.shipping;
    if (!ship || ship.reached || !cart || !Number.isSafeInteger(cart.total_price)) return false;
    if (quote.mode !== 'native') return false;
    var total = null;
    // Free shipping counted before discounts: the swap is exact list-price arithmetic.
    if (ship.subtotalBasis === 'original_total_price') {
      var unit = line && Number.isSafeInteger(line.original_price) ? line.original_price : null;
      if (unit === null || !Number.isSafeInteger(cart.original_total_price) || !Number.isSafeInteger(target.variant && target.variant.price)) return false;
      return cart.original_total_price - unit + target.variant.price >= ship.threshold;
    }
    if (typeof project === 'function' && line && line.key != null) {
      try { total = project(cart, [{key:line.key, quantity:1}], [{variant:target.variant, quantity:1}]); } catch (_) { total = null; }
    }
    return Number.isSafeInteger(total) && total >= ship.threshold;
  }
  // Fill a sachet gap from the engine's ordered pool (flavours the shopper does not
  // have first), skipping anything declined in this session.
  function fillSachets(data, opportunity, gap, skip, existing, allowed) {
    var pool = (Array.isArray(opportunity.pool) ? opportunity.pool : opportunity.entries || []).filter(function (p) {
      return data.variants[id(p.variantId)] && !(skip && skip[id(p.variantId)]);
    });
    var entries = [], left = gap;
    pool.forEach(function (p) { if (left > 0) { entries.push({variant:data.variants[id(p.variantId)], quantity:1, unseen:p.unseen !== false}); left--; } });
    if (left > 0 && entries.length) {
      var first = entries[0];
      if (!allowed || allowed(first.variant, first.quantity + left, existing || {})) { first.quantity += left; left = 0; }
    }
    return left ? [] : entries;
  }
  // Cheapest item that provably closes the shipping gap and makes sense here.
  function shippingCandidate(data, cart, quote, skip, avoid) {
    var o = quote && Array.isArray(quote.opportunities) && quote.opportunities.find(function (item) { return shippingCloses(item); });
    if (!o) return null;
    var hasBox = cartHasBox(data, cart), ids = Array.isArray(o.candidates) && o.candidates.length ? o.candidates : [o.candidateVariantId];
    for (var i = 0; i < ids.length; i++) {
      var variant = data.variants[id(ids[i])];
      if (!variant || (skip && skip[id(ids[i])]) || (avoid && avoid[id(ids[i])])) continue;
      if (hasBox && isBoxVariant(variant)) continue;
      return {variant:variant, gap:o.gap};
    }
    return null;
  }
  function sum(entries) { return entries.reduce(function (total, e) { return total + e.variant.price * e.quantity; }, 0); }

  // Drawer cross-sell: up to four single sachets the cart does not hold, in catalog order
  // (the theme's drawer_upsell_handles first). Flavours the cart lacks, as a sachet or inside
  // a box, come first, and one per product before a second flavour of the same product.
  function crossSell(input) {
    var data = input.catalog, cart = input.cart, merch = input.merch || {}, skip = input.skip || {}, avoid = input.avoid || {};
    if (!data || !Array.isArray(data.products) || !cart || !Array.isArray(cart.items)) return [];
    var existing = merch.quantities ? merch.quantities(cart) : {}, allowed = merch.allowed, covered = {}, owned = {}, pool = [];
    cart.items.forEach(function (line) {
      var v = data.variants[id(line.variant_id || line.id)];
      if (!v) return;
      if (v.product.recipe) Object.keys(v.product.recipe).forEach(function (key) { covered[key] = true; });
      else { covered[id(v.id)] = true; owned[id(v.product.id)] = true; }
    });
    data.products.forEach(function (product, order) {
      if (product.recipe) return;
      product.variants.forEach(function (variant) {
        var key = id(variant.id);
        if (inCart(cart, key) || skip[key] || avoid[key] || (allowed && !allowed(variant, 1, existing))) return;
        pool.push({variant:variant, order:order, covered:covered[key] ? 1 : 0, owned:owned[id(product.id)] ? 1 : 0});
      });
    });
    pool.sort(function (a, b) { return a.covered - b.covered || a.owned - b.owned || a.order - b.order; });
    var picks = [], used = {};
    pool.forEach(function (c) { if (picks.length < CROSS_SELL && !used[id(c.variant.product.id)]) { used[id(c.variant.product.id)] = true; picks.push(c); } });
    pool.forEach(function (c) { if (picks.length < CROSS_SELL && picks.indexOf(c) === -1) picks.push(c); });
    return picks.map(function (c) { return {variant:c.variant, quantity:1}; });
  }

  // Everything the drawer offers for one cart: an upgrade per box line and at most two
  // one-click cards. Pure, so the same rules are unit tested and rendered.
  function drawerPlan(input) {
    var data = input.catalog, cart = input.cart, quote = input.quote, merch = input.merch || {}, skip = input.skip || {};
    var plan = {upgrades:{}, cards:[]};
    if (!data || !cart || !Array.isArray(cart.items) || !cart.items.length) return plan;
    var existing = merch.quantities ? merch.quantities(cart) : {}, allowed = merch.allowed;
    var exclude = function (box) { return !!skip[id(box.variant.id)] || inCart(cart, box.variant.id); };
    cart.items.forEach(function (line) {
      var box = boxFor(data, line.variant_id || line.id);
      if (!box || (merch.eligibleBoxLine && !merch.eligibleBoxLine(line, box))) return;
      var target = nextBox(data, box, existing, allowed, exclude);
      // An exchange released while its new box may still land is not offered again for a while.
      if (!target || (input.held && input.held(box.variant.id, target.variant.id))) return;
      plan.upgrades[line.key] = {line:line, box:box, target:target, difference:target.variant.price - box.variant.price,
        freeShipping:swapReachesShipping(cart, quote, box, target, line, input.project)};
    });
    if (!quote) return plan;
    var upgrades = Object.keys(plan.upgrades).map(function (key) { return plan.upgrades[key]; }), avoid = {};
    upgrades.forEach(function (u) { avoid[id(u.target.variant.id)] = true; });
    var goal = quote.nextGoal;
    var fill = goal && goal.kind === 'sachets' && !goal.reached && goal.current >= 1 && goal.gap <= 3 &&
      quote.opportunities.find(function (o) { return o.kind === 'complete-sachets'; });
    if (fill) {
      var entries = fillSachets(data, fill, goal.gap, skip, existing, allowed);
      if (entries.length) {
        plan.cards.push({kind:'sachets', entries:entries, price:sum(entries), goal:goal});
        entries.forEach(function (e) { avoid[id(e.variant.id)] = true; });
      }
    }
    // A box swap that already reaches free shipping is the shipping offer; no second card.
    // The filler must also be proportionate: never more than twice what is in the cart.
    if (!upgrades.some(function (u) { return u.freeShipping; }) && plan.cards.length < 2) {
      var ship = shippingCandidate(data, cart, quote, skip, avoid);
      if (ship && Number.isSafeInteger(cart.total_price) && ship.variant.price <= cart.total_price * DRAWER_PRICE_RATIO) {
        plan.cards.push({kind:'shipping', entries:[{variant:ship.variant, quantity:1}], price:ship.variant.price, gap:ship.gap});
      }
    }
    return plan;
  }
  // Pick the one popup offer that fits what was just added, or none at all.
  function chooseOffer(input) {
    var data = input.catalog, cart = input.cart, quote = input.quote, added = input.added || [], merch = input.merch, skip = input.skip || {};
    if (!data || !cart || !Array.isArray(cart.items)) return null;
    var existing = merch && merch.quantities ? merch.quantities(cart) : {};
    var allowed = merch && merch.allowed;
    var exclude = function (box) { return !!skip[id(box.variant.id)] || inCart(cart, box.variant.id); };
    var addedValue = 0;
    added.forEach(function (row) { var v = data.variants[id(row.id)]; if (v) addedValue += v.price * (Number(row.quantity) || 1); });
    for (var i = 0; i < added.length; i++) {
      var box = boxFor(data, added[i].id);
      if (!box) continue;
      var target = nextBox(data, box, existing, allowed, exclude);
      if (target && input.held && input.held(box.variant.id, target.variant.id)) target = null;
      var lines = cart.items.filter(function (line) {
        return id(line.variant_id || line.id) === id(box.variant.id) && (!merch || !merch.eligibleBoxLine || merch.eligibleBoxLine(line, box));
      });
      if (target && lines.length === 1) {
        return {kind:'upgrade', box:box, target:target, line:lines[0], difference:target.variant.price - box.variant.price,
          extras:extras(data, box, target), freeShipping:swapReachesShipping(cart, quote, box, target, lines[0], input.project)};
      }
      // A box without a next step falls through to the other offers.
    }
    if (!quote) return null;
    var goal = quote.nextGoal;
    var addedSachet = added.some(function (row) { var v = data.variants[id(row.id)]; return v && !isBoxVariant(v); });
    if (goal && goal.kind === 'sachets' && !goal.reached && goal.gap <= 3 && addedSachet) {
      var fill = quote.opportunities.find(function (o) { return o.kind === 'complete-sachets'; });
      var entries = fill ? fillSachets(data, fill, goal.gap, skip, existing, allowed) : [];
      if (entries.length) return {kind:'sachets', entries:entries, goal:goal, price:sum(entries),
        unseen:entries.every(function (e) { return e.unseen; })};
    }
    var ship = shippingCandidate(data, cart, quote, skip, {});
    if (ship && ship.gap <= POPUP_SHIPPING_GAP && addedValue > 0 && ship.variant.price <= addedValue * POPUP_PRICE_RATIO) {
      return {kind:'shipping', entries:[{variant:ship.variant, quantity:1}], gap:ship.gap, price:ship.variant.price};
    }
    return null;
  }
  // "Dare Box · Default" reads as "Dare Box"; a sachet keeps its flavour.
  function variantName(variant) {
    var title = isBoxVariant(variant) ? shortTitle(variant.product.title) : variant.product.title;
    return title + (variant.title && variant.title !== 'Default Title' ? ' · ' + variant.title : '');
  }
  // Everything an open popup states, as one serializable descriptor: its kind, the line it
  // swaps, every item with name, quantity, price and image, counts, gap, tier, the unseen
  // flavour claim and the free shipping claim. The popup is drawn from it alone, and an open
  // popup holds only while the same descriptor is quoted again.
  function popupDescriptor(offer) {
    function item(entry) {
      return {id:id(entry.variant.id), name:variantName(entry.variant), quantity:entry.quantity, price:entry.variant.price, image:entry.variant.image || ''};
    }
    if (offer.kind === 'upgrade') {
      return {kind:'upgrade', line:offer.line && offer.line.key, box:{id:id(offer.box.variant.id), count:offer.box.count},
        target:{id:id(offer.target.variant.id), title:shortTitle(offer.target.title), count:offer.target.count, price:offer.target.variant.price,
          image:offer.target.variant.image || ''}, difference:offer.difference, extras:(offer.extras || []).map(item), freeShipping:!!offer.freeShipping};
    }
    if (offer.kind === 'sachets') {
      return {kind:'sachets', gap:offer.goal.gap, percent:offer.goal.percent, unseen:!!offer.unseen, entries:offer.entries.map(item), price:offer.price};
    }
    return {kind:offer.kind, gap:offer.gap, entries:offer.entries.map(item), price:offer.price};
  }
  function offerVariantIds(offer) {
    if (!offer) return [];
    if (offer.kind === 'upgrade') return [id(offer.target.variant.id)];
    return (offer.entries || []).map(function (e) { return id(e.variant.id); });
  }

  function mount(win) {
    var doc = win.document, node = doc.getElementById('grigCartUpsellConfig'), config;
    if (!node || !win.GRIGCart || !win.GRIGMerch) return;
    try { config = JSON.parse(node.textContent); } catch (_) { return; }
    var strings = config.strings || {}, drawer = doc.getElementById('drawer'), dialog = doc.getElementById('grigUpsell');
    var data = null, loading = null, busy = false, deciding = false, popupOffer = null, popupAdded = [], accepted = false, retired = false, closeOpensDrawer = true;
    // The popup's own accept is running: the popup tells what it does (its status, its alert, or
    // the drawer it gives way to).
    var accepting = false;
    var format, formatWhole;
    try {
      format = new Intl.NumberFormat(config.locale, {style:'currency', currency:config.currency || 'EUR', minimumFractionDigits:config.currency === 'CZK' ? 0 : 2, maximumFractionDigits:2});
      formatWhole = new Intl.NumberFormat(config.locale, {style:'currency', currency:config.currency || 'EUR', minimumFractionDigits:0, maximumFractionDigits:0});
    } catch (_) { return; }
    function money(cents) { return format.format(cents / 100); }
    // Differences and gaps read better without zero cents: "+13 €", not "+13,00 €".
    function moneyShort(cents) { return cents % 100 === 0 ? formatWhole.format(cents / 100) : money(cents); }
    function text(key, values) {
      var value = strings[key] || '';
      Object.keys(values || {}).forEach(function (k) { value = value.split('{' + k + '}').join(String(values[k])); });
      return win.grigDecode ? win.grigDecode(value) : value;
    }
    function plural(prefix, count) {
      var form = new Intl.PluralRules(config.locale).select(count);
      return prefix + (form === 'one' ? 'one' : form === 'few' ? 'few' : 'many');
    }
    // Czech "ze 3", "z 5": the preposition follows the spoken numeral.
    function hint(from, to) { return text([2, 3, 4, 6, 7].indexOf(from) >= 0 ? 'upgrade_hint_ze' : 'upgrade_hint', {from:from, to:to}); }
    function el(tag, cls, content) { var n = doc.createElement(tag); if (cls) n.className = cls; if (content != null) n.textContent = content; return n; }
    function imageURL(value) { try { var u = new URL(value, win.location.href); return u.protocol === 'https:' ? u.href : ''; } catch (_) { return ''; } }
    function productURL(value) { try { var u = new URL(value, win.location.href); return u.protocol === 'https:' && u.origin === win.location.origin ? u.href : ''; } catch (_) { return ''; } }
    function preview() { return !!(win.GRIGOffers && typeof win.GRIGOffers.preview === 'function' && win.GRIGOffers.preview()); }
    var name = variantName;
    function interaction(event, kind, surface, entries) {
      doc.dispatchEvent(new win.CustomEvent('grig:offer-interaction', {detail:{event:event, surface:surface, kind:kind,
        variantIds:entries.map(function (e) { return id(e.variant.id); }), quantities:entries.map(function (e) { return e.quantity; }),
        valueCents:entries.reduce(function (total, e) { return total + e.variant.price * e.quantity; }, 0), currency:config.currency}}));
    }
    // The merchandising loader's freshness rule (30 s from its loadedAt stamp): every
    // swap price, suggestion and popup decision is quoted only with a fresh catalog.
    var fresh = win.GRIGMerch.fresh;
    function catalog(force) {
      if (!force && fresh(data)) return Promise.resolve(data);
      if (!loading) loading = win.GRIGMerch.catalog(force === true).then(function (result) { data = result; return result; }).finally(function () { loading = null; });
      return loading;
    }
    function quote(cart) {
      if (!cart || !data || !win.GRIGOffers || typeof win.GRIGOffers.quote !== 'function') return null;
      try { return win.GRIGOffers.quote(cart, data); } catch (_) { return null; }
    }
    /* Session memory: declined offers and swapped-away boxes are not offered again. */
    function skipped() {
      try {
        var list = JSON.parse(win.sessionStorage.getItem(SKIP_KEY) || '[]'), map = {};
        (Array.isArray(list) ? list : []).forEach(function (v) { if (/^\d{1,20}$/.test(String(v))) map[String(v)] = true; });
        return map;
      } catch (_) { return {}; }
    }
    function skip(ids) {
      try {
        var map = skipped();
        ids.forEach(function (v) { if (/^\d{1,20}$/.test(id(v))) map[id(v)] = true; });
        win.sessionStorage.setItem(SKIP_KEY, JSON.stringify(Object.keys(map).slice(-40)));
      } catch (_) { /* Without storage every offer simply stays available. */ }
    }
    // The engine's native projection of Shopify's total after a hypothetical change;
    // null (no claim) whenever the offer layer or a model is unavailable.
    function project(cart, removals, entries) {
      if (!cart || !data || !win.GRIGOffers || typeof win.GRIGOffers.projectNative !== 'function') return null;
      try { return win.GRIGOffers.projectNative(cart, data, entries, removals); } catch (_) { return null; }
    }
    // A cart no read has confirmed: a change in flight (busy), a write that may still land, a
    // period boundary. Nothing is offered or claimed for it: no box swap, no card, no popup.
    function unconfirmed() { return win.GRIGCart.pending === true; }
    // A box exchange released while its new box may still land (GRIGCart holds it for a while).
    function held(source, target) { return typeof win.GRIGCart.swapHeld === 'function' && win.GRIGCart.swapHeld(source, target); }
    function plan(cart) {
      return drawerPlan({catalog:data, cart:cart, quote:quote(cart), merch:win.GRIGMerch, skip:skipped(), project:project, held:held});
    }
    if (drawer && !preview()) drawer.classList.add('gdr-owned');

    /* ---------- Drawer ---------- */
    function labelFor(title) {
      var n = config.native || {};
      if (n.sachets && title === n.sachets.title) return text('discount_sachets', {percent:n.sachets.percent});
      if (n.bf && n.bf.base && (title === n.bf.base.title || (n.bf.bonus && title === n.bf.bonus.title))) return text('discount_bf');
      return title;
    }
    function renderSummary(cart) {
      var box = doc.getElementById('gdrSummary'), count = doc.getElementById('drCount');
      if (count) { count.textContent = cart && cart.item_count ? '(' + cart.item_count + ')' : ''; count.hidden = !(cart && cart.item_count); }
      if (drawer) drawer.classList.toggle('gdr-is-empty', !!cart && !cart.item_count);
      if (!box) return;
      box.replaceChildren();
      // A cart no read has confirmed (GRIGCart.pending) shows no discount row of its own.
      var rows = cart && cart.item_count && !win.GRIGCart.pending ? discounts(cart) : [];
      if (!rows.length || !Number.isSafeInteger(cart.original_total_price)) { box.hidden = true; return; }
      function row(label, value, cls) { var wrap = el('div', cls); wrap.append(el('dt', '', label), el('dd', '', value)); box.append(wrap); }
      row(text('subtotal'), money(cart.original_total_price), 'gdr-sub');
      rows.forEach(function (r) { row(labelFor(r.title), '−' + money(r.amount), 'gdr-discount'); });
      box.hidden = false;
    }
    // Every one-click write here (drawer swap and cards, popup accept, PDP upgrade step) is off
    // while one runs or while no read confirms the cart (a write that may still land, a period
    // boundary); an upgrade step for a sold out box stays off.
    function setBusy(value) {
      busy = value;
      doc.querySelectorAll('[data-gdr-action],[data-gup-accept],[data-gpu-button]').forEach(function (b) {
        b.disabled = value || unconfirmed() || b.hasAttribute('data-gpu-unavailable') || stepHeld(b.closest('[data-gpu]'));
      });
      syncStepWait(); syncPopupWait();
    }
    // Why the popup's held accept waits, right above it, in the popup's own live status: a wait
    // that holds the cart for longer than a moment (GRIGCart.wait). While the popup is open it is
    // told only here: the drawer's and the PDP step's notices lie beneath it, and no toast repeats
    // what the popup's own accept is doing (grig.js).
    function syncPopupWait() {
      var node = dialog && dialog.querySelector('[data-gup-wait]'), accept = dialog && dialog.querySelector('[data-gup-accept]');
      if (!node) return;
      var copy = dialog.open && accept && accept.disabled && win.GRIGCart.wait || '';
      if (node.textContent !== copy) node.textContent = copy;
      node.hidden = !copy;
    }
    function upgradeButton(line, offer) {
      var button = el('button', 'gdr-upgrade');
      button.type = 'button'; button.dataset.gdrAction = 'upgrade'; button.dataset.gdrKey = line.key;
      button.dataset.gdrTarget = id(offer.target.variant.id);
      var sub = hint(offer.box.count, offer.target.count) + (offer.freeShipping ? text('upgrade_shipping') : '');
      button.append(el('span', 'gdr-upgrade-main', text('upgrade', {target:shortTitle(offer.target.title), amount:moneyShort(offer.difference)})),
        el('span', 'gdr-upgrade-sub', sub));
      button.disabled = busy || unconfirmed();
      return button;
    }
    // Decorations are drawn only where they differ (GRIGMerch.redraw): a catalog refresh
    // that decides the same leaves the focused swap or card button in place.
    var redraw = win.GRIGMerch.redraw;
    function decorateLines(cart, current) {
      if (!drawer || !cart || preview()) return;
      drawer.querySelectorAll('.dr-item[data-dr-key]').forEach(function (item) {
        var slot = item.querySelector('[data-dr-extra]');
        if (!slot) return;
        var offer = current.upgrades[item.dataset.drKey];
        redraw(slot, offer ? [upgradeButton(offer.line, offer)] : [], null);
      });
    }
    // Compact row: thumbnails, what it unlocks, exactly what gets added, one quiet button.
    function suggestionCard(kind, title, entries, price) {
      var card = el('article', 'gdr-card gdr-card--' + kind), media = el('span', 'gdr-card-media');
      media.setAttribute('aria-hidden', 'true');
      entries.slice(0, 2).forEach(function (entry) {
        var src = imageURL(entry.variant.image);
        if (src) { var img = el('img', 'gdr-card-img'); img.src = src; img.alt = ''; img.width = 44; img.height = 44; img.decoding = 'async'; media.append(img); }
      });
      var copy = el('span', 'gdr-card-copy');
      copy.append(el('span', 'gdr-card-title', title),
        el('span', 'gdr-card-names', entries.map(function (e) { return (e.quantity > 1 ? e.quantity + ' × ' : '') + name(e.variant); }).join(', ')));
      var button = el('button', 'gdr-add', text('add', {amount:money(price)}));
      button.type = 'button'; button.dataset.gdrAction = kind;
      button.setAttribute('aria-label', text('add_label', {items:entries.map(function (e) { return e.quantity + ' × ' + name(e.variant); }).join(', '), amount:money(price)}));
      button.disabled = busy || unconfirmed();
      card.append(media, copy, button);
      return card;
    }
    var currentSuggestions = {};
    function drawerSuggestions(cart, current) {
      var section = doc.getElementById('gdrSuggest');
      if (!section) return;
      var list = section.querySelector('[data-gdr-suggest-list]'), statusNode = section.querySelector('[data-gdr-status]'), cards = [];
      currentSuggestions = {};
      // Up to two cards (the sachet top-up before the shipping filler), next to any box swap at a
      // line (owner, 24 Oct 2026: more upsells in the drawer).
      var budget = DRAWER_OFFERS;
      if (cart && cart.item_count && !preview()) current.cards.slice(0, budget).forEach(function (card) {
        currentSuggestions[card.kind] = card;
        var title = card.kind === 'sachets' ? text(card.entries.length > 1 || card.entries[0].quantity > 1 ? 'sachets_card_many' : 'sachets_card_one', {percent:card.goal.percent}) :
          text('shipping_title');
        cards.push(suggestionCard(card.kind, title, card.entries, card.price));
      });
      redraw(list, cards, statusNode);
      section.hidden = !cards.length && !(statusNode && statusNode.textContent);
    }
    // "Throw in one more": up to four single sachets the cart lacks, one tap each (crossSell).
    var currentCross = {};
    function crossTile(entry) {
      var variant = entry.variant, tile = el('article', 'gdr-tile'), src = imageURL(variant.image), href = productURL(variant.url);
      // The photo and the name open the product (owner, 25 Oct 2026); the photo link stays out of the tab order.
      var media = href ? el('a', 'gdr-tile-media') : tile;
      if (href) { media.href = href; media.tabIndex = -1; media.setAttribute('aria-hidden', 'true'); tile.append(media); }
      if (src) { var img = el('img', 'gdr-tile-img'); img.src = src; img.alt = ''; img.width = 48; img.height = 48; img.decoding = 'async'; media.append(img); }
      else media.append(el('span', 'gdr-tile-img'));
      var copy = el('span', 'gdr-tile-copy'), title = el(href ? 'a' : 'span', 'gdr-tile-name', name(variant).replace(/(\d) g\b/g, '$1\u00a0g'));
      if (href) title.href = href;
      copy.append(title, el('span', 'gdr-tile-price', money(variant.price)));
      var button = el('button', 'gdr-tile-add', text('cross_add'));
      button.type = 'button'; button.dataset.gdrAction = 'cross'; button.dataset.gdrKey = id(variant.id);
      button.setAttribute('aria-label', text('add_label', {items:'1 × ' + name(variant), amount:money(variant.price)}));
      button.disabled = busy || unconfirmed();
      tile.append(copy, button);
      return tile;
    }
    // The same picks in the drawer and on the cart page (data-gdr-cross-page, under its progress bars).
    function drawerCross(cart, current) {
      var sections = Array.prototype.slice.call(doc.querySelectorAll('#gdrCross, [data-gdr-cross-page]'));
      if (!sections.length) return;
      var avoid = {}, picks = [];
      currentCross = {};
      Object.keys(currentSuggestions).forEach(function (kind) { currentSuggestions[kind].entries.forEach(function (e) { avoid[id(e.variant.id)] = true; }); });
      Object.keys(current.upgrades || {}).forEach(function (key) { avoid[id(current.upgrades[key].target.variant.id)] = true; });
      if (cart && cart.item_count && !preview()) picks = crossSell({catalog:data, cart:cart, merch:win.GRIGMerch, skip:skipped(), avoid:avoid});
      // The cart page also offers the boxes (owner 24 Oct 2026): up to two the cart does not hold,
      // ahead of the sachets, while the cart has no box yet (a box line gets its upgrade instead).
      var boxes = [];
      if (cart && cart.item_count && !preview() && data && Array.isArray(data.products) &&
          !cart.items.some(function (line) { return isBoxVariant(data.variants[id(line.variant_id || line.id)]); })) {
        var existingQty = win.GRIGMerch && win.GRIGMerch.quantities ? win.GRIGMerch.quantities(cart) : {};
        data.products.forEach(function (product) {
          var variant = product.recipe && product.variants && product.variants[0];
          if (!variant || boxes.length >= 2 || inCart(cart, id(variant.id)) || avoid[id(variant.id)] ||
            (win.GRIGMerch && win.GRIGMerch.allowed && !win.GRIGMerch.allowed(variant, 1, existingQty))) return;
          boxes.push({variant:variant, quantity:1});
        });
      }
      picks.concat(boxes).forEach(function (entry) { currentCross[id(entry.variant.id)] = entry; });
      sections.forEach(function (section) {
        var list = section.id === 'gdrCross' ? picks : boxes.concat(picks).slice(0, 6);
        redraw(section.querySelector('[data-gdr-cross-list]'), list.map(crossTile), null);
        section.hidden = !list.length;
      });
    }
    function decorate(cart) {
      var current = plan(cart);
      decorateLines(cart, current);
      drawerSuggestions(cart, current);
      drawerCross(cart, current);
    }
    // Without a cart that was actually read back nothing may stay offered: no box swap,
    // no suggestion card, each priced and decided for a cart that is no longer known.
    function clearDecorations() {
      decorateLines({items:[]}, {upgrades:{}});
      drawerSuggestions(null, {cards:[]});
      drawerCross(null, {upgrades:{}});
    }
    function refreshDrawer(cart) {
      cart = cart || win.GRIGCart.state;
      renderSummary(cart);
      // Nothing is offered for a cart no read has confirmed; the decorations return when it is.
      if (!cart || unconfirmed()) { clearDecorations(); return; }
      // Decorate synchronously while the catalog is fresh, so a re-render never flickers;
      // an expired one prices nothing until the fresh one lands.
      if (fresh(data)) decorate(cart); else clearDecorations();
      catalog(false).then(function () {
        var latest = win.GRIGCart.state;
        if (!latest || unconfirmed() || !fresh(data)) { clearDecorations(); return; }
        decorate(latest);
        syncUpgradeModule(latest);
      }).catch(function () { /* Decorations are optional; the cart still works. */ });
    }
    // The cart a status was written for: re-reads that show the same cart (a swap left in
    // review is read again for a while) keep it; a different cart retires it after a while.
    var statusAt = 0, statusCart = '';
    function cartKey(cart) {
      return cart && Array.isArray(cart.items) ? [cart.total_price].concat(cart.items.map(function (line) { return line.key + ':' + line.quantity; })).join('|') : '';
    }
    // Keyboard focus is moved only onto what the shopper sees: the target is scrolled into the
    // visible part of the page and of the drawer's body (the status sits below every line), now
    // and once more when the drawer is drawn for the cart it reports (its footer may grow).
    function focusInView(node) {
      function show() { if (doc.activeElement === node) { try { node.scrollIntoView({block:'nearest', inline:'nearest'}); } catch (_) { /* Focus is a courtesy. */ } } }
      try { node.focus({preventScroll:true}); } catch (_) { return; }
      show(); win.requestAnimationFrame(show);
    }
    function status(message, isError) {
      var node = doc.querySelector('#gdrSuggest [data-gdr-status]');
      if (!node) return;
      statusAt = Date.now(); statusCart = cartKey(win.GRIGCart.state);
      node.textContent = message; node.classList.toggle('is-error', !!isError);
      if (message) { var section = doc.getElementById('gdrSuggest'); if (section) section.hidden = false; if (win.GRIGCart.open) focusInView(node); }
    }
    // The fresh catalog a one-tap write is checked against starts loading at the tap, alongside
    // GRIGCart's read of the cart, instead of after it (a whole round trip faster).
    function freshCatalog() { var loaded = catalog(true); loaded.catch(function () {}); return loaded; }
    async function addEntries(entries, kind, surface, button) {
      var expected = entries.map(function (e) { return {id:id(e.variant.id), quantity:e.quantity, price:e.variant.price}; });
      var checked = freshCatalog();
      var result = await win.GRIGCart.addItems(expected.map(function (e) { return {id:e.id, quantity:e.quantity}; }), {
        source:surface + '_' + kind, button:button, openDrawer:surface !== 'pdp' && surface !== 'cart',
        validate:async function (freshCart) {
          var fresh = await checked, existing = win.GRIGMerch.quantities(freshCart || {items:[]});
          expected.forEach(function (e) {
            var v = fresh.variants[e.id];
            if (!v || v.price !== e.price || !win.GRIGMerch.allowed(v, e.quantity, existing)) { var error = new Error('changed'); error.code = 'changed'; throw error; }
          });
        }
      });
      if (result && result.ok) interaction('add', kind, surface, entries);
      return result;
    }
    function swapSpec(line, box, target) {
      return {sourceKey:line.key, sourceVariantId:box.variant.id, sourceQuantity:line.quantity, targetVariantId:target.variant.id,
        sourceProperties:line.properties || {}, sourceRecipe:box.recipe, targetRecipe:target.recipe};
    }
    async function upgrade(line, box, target, surface, button) {
      var difference = target.variant.price - box.variant.price, checked = freshCatalog();
      var result = await win.GRIGCart.swapBox(swapSpec(line, box, target), {source:surface + '_box_upgrade', button:button, openDrawer:surface !== 'pdp',
        validate:async function () {
          var fresh = await checked, b = boxFor(fresh, box.variant.id), t = boxFor(fresh, target.variant.id);
          if (!b || !t || t.variant.price - b.variant.price !== difference) { var error = new Error('swap_changed'); error.code = 'swap_changed'; throw error; }
        }});
      if (result && result.ok) {
        // The box that was swapped away is not offered back in this session.
        skip([box.variant.id]);
        interaction('upgrade', 'box_upgrade', surface, [{variant:target.variant, quantity:1}]);
      }
      return result;
    }
    function eligibleLine(cart, box) {
      var lines = cart && box ? cart.items.filter(function (l) { return id(l.variant_id || l.id) === id(box.variant.id) && win.GRIGMerch.eligibleBoxLine(l, box); }) : [];
      return lines.length === 1 ? lines[0] : null;
    }
    async function onAction(event) {
      var button = event.target.closest('[data-gdr-action]');
      if (!button || busy || unconfirmed()) return;
      var kind = button.dataset.gdrAction, cart = win.GRIGCart.state;
      setBusy(true); status('');
      try {
        var result;
        if (kind === 'upgrade') {
          var current = cart && plan(cart), offer = current && current.upgrades[button.dataset.gdrKey];
          if (!offer || id(offer.target.variant.id) !== button.dataset.gdrTarget) throw new Error('changed');
          result = await upgrade(offer.line, offer.box, offer.target, 'drawer', button);
          // A swap whose outcome could not be confirmed is not a failure: the shopper reviews the cart.
          var review = !!result && result.reason === 'swap_review';
          // Nor is one refused while an earlier write may still land: GRIGCart tells the shopper.
          // One left in review while a step of it may still land is told once, by GRIGCart: the
          // wait next to the held checkout, then how it ended. No second notice here.
          var refused = !!result && result.unknown === true, told = review && result.reviewTold === true;
          status(result && result.ok ? text('upgrade_done', {target:shortTitle(offer.target.title)}) : refused || told ? '' : text(review ? 'swap_review' : result && result.reason === 'swap_changed' ? 'changed' : 'error'), !(result && result.ok) && !review && !refused);
        } else {
          var suggestion = kind === 'cross' ? currentCross[button.dataset.gdrKey] && {entries:[currentCross[button.dataset.gdrKey]]} : currentSuggestions[kind];
          if (!suggestion) throw new Error('changed');
          // On the cart page the page itself shows the new cart: no drawer slides in (owner 24 Oct 2026).
          result = await addEntries(suggestion.entries, kind, button.closest('[data-gdr-cross-page]') ? 'cart' : 'drawer', button);
          // An addition whose outcome is unknown is neither: the drawer says the cart is being checked.
          var unknown = !!result && result.unknown === true;
          status(result && result.ok ? text('added') : unknown ? '' : text(result && result.reason === 'changed' ? 'changed' : 'error'), !(result && result.ok) && !unknown);
        }
      } catch (_) { status(text('changed'), true); }
      finally { setBusy(false); refreshDrawer(); }
    }
    if (drawer) drawer.addEventListener('click', onAction);
    doc.querySelectorAll('[data-gdr-cross-page]').forEach(function (section) { section.addEventListener('click', onAction); });

    /* ---------- PDP upgrade step: swap the box in the cart, never add a second one ---------- */
    // Its exchange was released while the new box may still land: the step stays off a while.
    function stepHeld(section) {
      var form = section && section.querySelector('form[data-gpu-form]');
      return !!form && form.dataset.gpuMode === 'swap' && held(section.dataset.gpuSource, section.dataset.gpuTarget);
    }
    // Why the step is off, next to it: a wait that holds the cart for longer than a moment
    // (GRIGCart.wait), or its own exchange held while an earlier one's box may still land.
    function syncStepWait() {
      doc.querySelectorAll('[data-gpu]').forEach(function (section) {
        var node = section.querySelector('[data-gpu-wait]');
        if (!node) return;
        var copy = unconfirmed() && win.GRIGCart.wait || (stepHeld(section) && win.GRIG_I18N && win.GRIG_I18N.swapRelease) || '';
        if (node.textContent !== copy) node.textContent = copy;
        node.hidden = !copy;
      });
    }
    // A cart no read has confirmed decides nothing here: the step keeps what it showed, off.
    function syncUpgradeModule(cart) {
      doc.querySelectorAll('[data-gpu]').forEach(function (section) {
        var form = section.querySelector('form[data-gpu-form]'), button = form && form.querySelector('[data-gpu-button]');
        if (button) button.disabled = busy || unconfirmed() || button.hasAttribute('data-gpu-unavailable');
        if (!form || !button || !cart || !Array.isArray(cart.items) || unconfirmed()) return;
        var targetId = section.dataset.gpuTarget, hasTarget = inCart(cart, targetId), fromBox = section.dataset.gpuFromBox === 'true';
        // Until the catalog is known a box page cannot tell add from swap, so it waits.
        var mode = fromBox && !data ? 'pending' : 'add';
        // The next box is already in the cart: nothing left to upgrade here.
        section.hidden = hasTarget;
        if (!hasTarget && data && fromBox) {
          var box = boxFor(data, section.dataset.gpuSource), target = boxFor(data, targetId);
          if (box && target && eligibleLine(cart, box)) mode = 'swap';
        }
        form.dataset.gpuMode = mode;
        button.disabled = busy || button.hasAttribute('data-gpu-unavailable') || stepHeld(section);
        syncStepWait();
        var label = mode === 'swap' ? button.dataset.gpuSwapLabel : button.dataset.gpuAddLabel;
        if (label && button.textContent !== label) button.textContent = label;
      });
    }
    doc.addEventListener('submit', function (event) {
      var form = event.target && event.target.closest ? event.target.closest('form[data-gpu-form]') : null;
      var mode = form && form.dataset.gpuMode;
      if (mode !== 'swap' && mode !== 'pending') return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy || win.GRIGCart.busy || unconfirmed() || stepHeld(form.closest('[data-gpu]'))) return;
      if (mode === 'swap') { swapFromModule(form); return; }
      // Decide once the catalog is known: swap the box in the cart, or add the next box.
      catalog(false).catch(function () { return null; }).then(function () {
        if (busy || win.GRIGCart.busy || unconfirmed()) return;
        var section = form.closest('[data-gpu]'), cart = win.GRIGCart.state;
        if (cart) syncUpgradeModule(cart);
        if (form.dataset.gpuMode === 'pending') form.dataset.gpuMode = 'add';
        if (section.hidden) return;
        if (form.dataset.gpuMode === 'swap') swapFromModule(form);
        else if (typeof form.requestSubmit === 'function') form.requestSubmit(form.querySelector('[data-gpu-button]'));
      });
    }, true);
    async function swapFromModule(form) {
      var section = form.closest('[data-gpu]'), button = form.querySelector('[data-gpu-button]'), error = section.querySelector('[data-gpu-error]');
      var cart = win.GRIGCart.state, box = boxFor(data, section.dataset.gpuSource), target = boxFor(data, section.dataset.gpuTarget);
      var line = eligibleLine(cart, box), result = null;
      if (error) { error.hidden = true; error.textContent = ''; }
      setBusy(true);
      try {
        result = line && target ? await upgrade(line, box, target, 'pdp_upgrade', button) : null;
        if (result && result.ok) status(text('upgrade_done', {target:shortTitle(target.title)}));
      } catch (_) { result = null; }
      finally {
        setBusy(false);
        // An outcome that is unknown is no failure: the cart reads that it is being checked. A
        // review whose step may still land is told by GRIGCart (the wait next to the step, then
        // how it ended), once.
        var told = !!result && result.reason === 'swap_review' && result.reviewTold === true;
        if (!(result && result.ok) && !(result && result.unknown === true) && !told && error) { error.textContent = text(result && result.reason === 'swap_review' ? 'swap_review' : result && result.reason === 'swap_changed' || !line ? 'changed' : 'error'); error.hidden = false; }
        refreshDrawer();
      }
    }

    /* ---------- Upsell popup ---------- */
    function sessionAllows() {
      try { return win.sessionStorage.getItem(SESSION_KEY) === null; } catch (_) { return false; }
    }
    function remember() {
      try { win.sessionStorage.setItem(SESSION_KEY, String(Date.now())); return win.sessionStorage.getItem(SESSION_KEY) !== null; } catch (_) { return false; }
    }
    function visible(n) {
      if (!n || n.hidden || n.closest('[hidden]')) return false;
      var r = n.getBoundingClientRect(), style = win.getComputedStyle(n);
      return r.width > 0 && r.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
    }
    // Another overlay layer is open (GRIGLayers tells them apart: the menu, the cookie
    // preferences, the drawer, a dialog): no popup over it. The collapsed cookie notice is no
    // layer: most first visits add before answering it, and it waits under the popup.
    function otherOverlay() {
      var layer = win.GRIGLayers && win.GRIGLayers.open;
      if (layer && !(layer === 'dialog' && dialog && dialog.open)) return true;
      var consent = doc.getElementById('consent');
      if (win.GRIGCart.open || (visible(consent) && consent.classList.contains('grig-consent-expanded'))) return true;
      return Array.from(doc.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"]')).some(function (n) { return n !== dialog; });
    }
    function pdpForm(form) {
      return !!(form && form.matches && form.matches('form.lhero-buybar[data-grig-add], form.sa-form[data-grig-add], form#grig-pdp-form[data-grig-add]'));
    }
    function focusables() {
      return Array.from(dialog.querySelectorAll('button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])')).filter(function (n) { return n.getClientRects().length; });
    }
    function closePopup(openDrawer) {
      if (!dialog || !dialog.open) return;
      closeOpensDrawer = openDrawer !== false;
      dialog.close();
    }
    // One cleanup for every way the dialog closes, native closes included.
    function afterClose() {
      doc.documentElement.classList.remove('gup-open');
      syncPopupWait();
      var offer = popupOffer, wasAccepted = accepted, wasRetired = retired, open = closeOpensDrawer;
      popupOffer = null; accepted = false; retired = false; closeOpensDrawer = true;
      // A retired offer (it no longer held, or its outcome is unknown) was not declined.
      if (offer && !wasAccepted && !wasRetired) skip(offerVariantIds(offer));
      if (offer && !wasAccepted) refreshDrawer();
      // The drawer takes focus on its title; nothing hands it back to the add button.
      if (open) win.GRIGCart.openDrawer(true);
    }
    function offerList(items) {
      var list = el('ul', 'gup-items');
      items.forEach(function (item) {
        var li = el('li', 'gup-item'), src = imageURL(item.image);
        if (src) { var img = el('img', 'gup-img'); img.src = src; img.alt = ''; img.width = 64; img.height = 64; img.decoding = 'async'; li.append(img); }
        var body = el('span', 'gup-item-body');
        body.append(el('strong', 'gup-item-name', (item.quantity > 1 ? item.quantity + ' × ' : '') + item.name), el('span', 'gup-item-price', money(item.price * item.quantity)));
        li.append(body); list.append(li);
      });
      return list;
    }
    // The popup states only what its descriptor holds (popupDescriptor).
    function openPopup(offer, addedTitle, added) {
      var title = dialog.querySelector('[data-gup-title]'), copy = dialog.querySelector('[data-gup-text]');
      var slot = dialog.querySelector('[data-gup-offer]'), accept = dialog.querySelector('[data-gup-accept]'), error = dialog.querySelector('[data-gup-error]');
      var view = popupDescriptor(offer);
      dialog.querySelector('[data-gup-added]').textContent = text('popup_added', {product:addedTitle});
      slot.replaceChildren(); error.hidden = true; error.textContent = '';
      if (view.kind === 'upgrade') {
        var more = view.extras.map(function (e) { return e.name; });
        title.textContent = text('popup_upgrade_title', {target:view.target.title});
        copy.textContent = [text('popup_upgrade_text', {amount:moneyShort(view.difference), from:view.box.count, to:view.target.count}),
          more.length ? text('popup_upgrade_extras', {extras:more.join(', ')}) : '', view.freeShipping ? text('popup_upgrade_shipping') : ''].filter(Boolean).join(' ');
        var src = imageURL(view.target.image);
        var card = el('div', 'gup-upgrade');
        if (src) { var img = el('img', 'gup-img gup-img--box'); img.src = src; img.alt = ''; img.width = 88; img.height = 88; img.decoding = 'async'; card.append(img); }
        var body = el('span', 'gup-item-body');
        body.append(el('strong', 'gup-item-name', view.target.title), el('span', 'gup-item-price', money(view.target.price) + ' · ' + hint(view.box.count, view.target.count)));
        card.append(body); slot.append(card);
        accept.textContent = text('popup_upgrade_accept', {amount:moneyShort(view.difference)});
      } else if (view.kind === 'sachets') {
        title.textContent = text(plural('sachets_title_', view.gap), {count:view.gap, percent:view.percent});
        copy.textContent = view.unseen ? text(view.entries.length > 1 ? 'popup_sachets_text_many' : 'popup_sachets_text_one') : text('popup_sachets_text_any');
        slot.append(offerList(view.entries));
        accept.textContent = text('add', {amount:money(view.price)});
      } else {
        title.textContent = text('popup_shipping_title', {amount:moneyShort(view.gap)});
        copy.textContent = text('popup_shipping_text');
        slot.append(offerList(view.entries));
        accept.textContent = text('add', {amount:money(view.price)});
      }
      accept.disabled = false;
      popupOffer = offer; popupAdded = added || []; accepted = false; closeOpensDrawer = true;
      // A closing modal dialog gives focus back to what held it when it opened, here the add
      // button (a held or repeated key would add again): nothing holds it.
      if (doc.activeElement && doc.activeElement !== doc.body && typeof doc.activeElement.blur === 'function') doc.activeElement.blur();
      dialog.showModal();
      syncPopupWait();
      doc.documentElement.classList.add('gup-open');
      title.focus({preventScroll:true});
      interaction('impression', 'popup_' + offer.kind, 'pdp', offer.kind === 'upgrade' ? [{variant:offer.target.variant, quantity:1}] : offer.entries);
    }
    // The confirmation comes from Shopify's own answer to the add; no name, no popup.
    function addedName(detail) {
      var payload = detail && detail.payload, line = payload && (Array.isArray(payload.items) ? payload.items[0] : payload);
      var title = line && (line.product_title || line.title);
      if (typeof title === 'string' && title.trim()) {
        var variant = line.variant_title && line.variant_title !== 'Default Title' && line.product_title ? ' · ' + line.variant_title : '';
        return shortTitle(title) + variant;
      }
      var row = detail && detail.items && detail.items[0], v = row && data && data.variants[id(row.id)];
      return v ? name(v) : '';
    }
    // The popup is quoted on the cart GRIGCart holds now, after the latest addition, and
    // never while a change is pending (its cart is about to be replaced).
    function decide(detail) {
      if (!fresh(data) || !sessionAllows() || otherOverlay() || busy || win.GRIGCart.busy || win.GRIGCart.pending) return false;
      var cart = win.GRIGCart.state, addedTitle = addedName(detail);
      if (!addedTitle) return false;
      var offer = chooseOffer({catalog:data, cart:cart, quote:quote(cart), added:detail.items || [], merch:win.GRIGMerch, skip:skipped(), project:project, held:held});
      if (!offer || !remember()) return false;
      openPopup(offer, addedTitle, detail.items || []);
      return true;
    }
    // Everything an open popup states, serialized from the descriptor it is drawn from.
    function offerSignature(o) { return JSON.stringify(popupDescriptor(o)); }
    // An open popup holds only while the same decision is quoted again now: a fresh catalog,
    // a cart no change or boundary leaves pending, and the same offer for the same addition.
    function popupHolds() {
      var cart = win.GRIGCart.state;
      if (!popupOffer || !fresh(data) || !cart || win.GRIGCart.pending) return false;
      var again = chooseOffer({catalog:data, cart:cart, quote:quote(cart), added:popupAdded, merch:win.GRIGMerch, skip:skipped(), project:project, held:held});
      return !!again && offerSignature(again) === offerSignature(popupOffer);
    }
    // The cart was read again, the catalog expired or was replaced, or a boundary made the
    // cart pending while the popup is open: an offer that no longer holds is retired (closed
    // for the drawer, not recorded as declined). The shopper's own accept decides itself.
    // A check that comes while the shopper's own accept runs is queued, never dropped: it runs
    // once the attempt is over and did not close the popup itself.
    var revalidateQueued = false;
    function revalidatePopup() {
      if (!dialog || !dialog.open || !popupOffer || accepted) return;
      if (busy) { revalidateQueued = true; return; }
      revalidateQueued = false;
      if (popupHolds()) return;
      retired = true; closePopup(true);
    }
    // The shopper's own add may be followed by the one-time popup: on a product page from its
    // buy form, on the homepage and collections from a product card (owner, 24 Oct 2026). A
    // one-click add from an offer, a builder or the drawer never opens it.
    function popupAdd(detail) {
      if (!detail || !POPUP_PAGES[config.page]) return false;
      if (config.page === 'product') return detail.source === 'product' && pdpForm(detail.form);
      // Homepage cards report home, home_hero, home_video_hero or home-hotspots; collection cards product.
      return /^home/.test(String(detail.source || '')) || detail.source === 'product';
    }
    function afterAdd(detail) {
      if (!dialog || typeof dialog.showModal !== 'function' || preview() || !popupAdd(detail)) return false;
      if (!sessionAllows() || otherOverlay() || busy) return false;
      if (fresh(data)) return decide(detail);
      // The catalog expired while the shopper looked at the page. The decision waits for
      // the fresh one (the add already started it), at most POPUP_CATALOG_WAIT, while a
      // second tap on the add button adds nothing (pending). Without a popup the shopper
      // gets the usual feedback: crunch, thumbs and the drawer.
      deciding = true;
      var waited = new Promise(function (resolve) { win.setTimeout(resolve, POPUP_CATALOG_WAIT); });
      Promise.race([catalog(false), waited]).catch(function () {}).then(function () {
        deciding = false;
        if (decide(detail)) return;
        if (typeof detail.feedback === 'function') detail.feedback(); else win.GRIGCart.openDrawer(true);
      });
      return true;
    }
    if (dialog) {
      dialog.addEventListener('cancel', function (event) { event.preventDefault(); if (!busy) closePopup(true); });
      dialog.addEventListener('close', afterClose);
      dialog.querySelector('[data-gup-decline]').addEventListener('click', function () { if (!busy) closePopup(true); });
      dialog.addEventListener('click', function (event) {
        if (event.target !== dialog || busy) return;
        var r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) closePopup(true);
      });
      dialog.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab') return;
        var items = focusables();
        if (!items.length) { event.preventDefault(); return; }
        var first = items[0], last = items[items.length - 1], active = doc.activeElement;
        if (event.shiftKey && (active === first || items.indexOf(active) < 0)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (active === last || items.indexOf(active) < 0)) { event.preventDefault(); first.focus(); }
      });
      dialog.querySelector('[data-gup-accept]').addEventListener('click', async function (event) {
        var offer = popupOffer, button = event.currentTarget, error = dialog.querySelector('[data-gup-error]');
        if (!offer || busy || unconfirmed()) return;
        accepting = true; setBusy(true); error.hidden = true; revalidateQueued = false;
        interaction('click', 'popup_' + offer.kind, 'pdp', offer.kind === 'upgrade' ? [{variant:offer.target.variant, quantity:1}] : offer.entries);
        var result;
        try {
          if (offer.kind === 'upgrade') {
            var cart = win.GRIGCart.state, line = cart && cart.items.find(function (l) { return l.key === offer.line.key; }) ||
              cart && cart.items.find(function (l) { return id(l.variant_id || l.id) === id(offer.box.variant.id); });
            result = line ? await upgrade(line, offer.box, offer.target, 'pdp', button) : null;
          } else result = await addEntries(offer.entries, offer.kind, 'pdp', button);
        } catch (_) { result = null; }
        accepting = false; setBusy(false);
        if (result && result.ok) { accepted = true; closePopup(true); return; }
        // Its outcome is unknown, or GRIGCart tells how a swap in review ended: the drawer shows
        // it, not a second notice here.
        if (result && (result.unknown === true || result.reviewTold === true)) { retired = true; closePopup(true); return; }
        if (!dialog.open) return;
        // The cart or catalog changed while the attempt ran: an offer that no longer holds is
        // retired rather than left on screen with an error.
        if (revalidateQueued) { revalidatePopup(); if (!dialog.open) return; }
        error.textContent = text(result && result.reason === 'swap_review' ? 'swap_review' : result && (result.reason === 'changed' || result.reason === 'swap_changed') ? 'changed' : 'error');
        error.hidden = false; error.tabIndex = -1; focusInView(error);
      });
    }

    /* ---------- PDP flavour row: hide what is already in the cart ---------- */
    function syncFlavours(cart) {
      doc.querySelectorAll('[data-gfr-row]').forEach(function (row) {
        var max = Number(row.dataset.gfrMax) || 3, shown = 0;
        var inCartMap = {};
        (cart && cart.items || []).forEach(function (line) { inCartMap[id(line.variant_id || line.id)] = true; });
        row.querySelectorAll('[data-gfr-card]').forEach(function (card) {
          var show = !inCartMap[card.dataset.gfrVariant] && shown < max;
          card.hidden = !show; if (show) shown++;
        });
        row.dataset.gfrShown = String(shown);
        row.hidden = shown === 0;
      });
    }

    doc.addEventListener('grig:cart-rendered', function (event) {
      // A confirmation belongs to one action; later outside cart changes retire it.
      var note = doc.querySelector('#gdrSuggest [data-gdr-status]'), cart = event.detail && event.detail.cart;
      if (note && note.textContent && !busy && Date.now() - statusAt > 6000 && cartKey(cart) !== statusCart) { note.textContent = ''; note.classList.remove('is-error'); }
      refreshDrawer(cart); syncFlavours(cart); syncUpgradeModule(cart);
    });
    doc.addEventListener('grig:offers-updated', function () { refreshDrawer(); });
    // Re-apply disabled states; our own busy flag is released only by our own action. A
    // failed read-back clears the decorations through grig:cart-load-error below.
    // A product page refreshes an expired catalog as an addition starts, so the popup
    // decision after it is quoted fresh (without one in time there is no popup).
    doc.addEventListener('grig:cart-busy', function (event) {
      setBusy(busy);
      // The change is over: the cart it read back may be offered again (unless still pending).
      if (event.detail && !event.detail.busy && !busy) refreshDrawer();
      if (POPUP_PAGES[config.page] && event.detail && event.detail.busy && !fresh(data)) catalog(false).catch(function () {});
    });
    doc.addEventListener('grig:cart-load-error', function () { refreshDrawer(); revalidatePopup(); });
    doc.addEventListener('grig:cart-pending', function () { setBusy(busy); refreshDrawer(); revalidatePopup(); });
    doc.addEventListener('grig:cart-updated', revalidatePopup);
    // An expired catalog prices no swap and no card: they go at once. Opening the drawer
    // with one also starts the fresh catalog, in parallel with the cart read.
    doc.addEventListener('grig:catalog-expired', function () { if (!fresh(data)) clearDecorations(); revalidatePopup(); });
    doc.addEventListener('grig:catalog-loaded', function (event) {
      var loaded = event.detail && event.detail.catalog;
      if (!fresh(loaded)) return;
      data = loaded; refreshDrawer();
      if (win.GRIGCart.state) syncUpgradeModule(win.GRIGCart.state);
      revalidatePopup();
    });
    doc.addEventListener('grig:cart-open', function () {
      if (fresh(data)) return;
      clearDecorations();
      catalog(false).then(function () { refreshDrawer(); }).catch(function () {});
    });
    win.GRIGCartUpsell = {afterAdd:afterAdd, get active() { return !!(dialog && dialog.open); }, get pending() { return deciding; },
      get accepting() { return accepting && !!(dialog && dialog.open); }, refresh:refreshDrawer, close:closePopup,
      resetSession:function () { try { win.sessionStorage.removeItem(SESSION_KEY); win.sessionStorage.removeItem(SKIP_KEY); } catch (_) {} }};
    // Warm the catalog on product pages so a popup decision never waits on the network.
    if (config.page === 'product') catalog(false).then(function () { if (win.GRIGCart.state) syncUpgradeModule(win.GRIGCart.state); }).catch(function () {});
    if (win.GRIGCart.state) { refreshDrawer(win.GRIGCart.state); syncFlavours(win.GRIGCart.state); syncUpgradeModule(win.GRIGCart.state); }
  }
  return {mount:mount, nextBox:nextBox, chooseOffer:chooseOffer, drawerPlan:drawerPlan, crossSell:crossSell, discounts:discounts, boxFor:boxFor, shortTitle:shortTitle, popupDescriptor:popupDescriptor,
    swapReachesShipping:swapReachesShipping, POPUP_SHIPPING_GAP:POPUP_SHIPPING_GAP};
});
